<script setup lang="ts">
import ReactiveLogo from '@/components/Assets/ReactiveLogo.vue'
import { InputField } from '@/components/Fields'
import GuestLayout from '@/components/Layouts/GuestLayout.vue'
import StackLayout from '@/components/Layouts/StackLayout.vue'
import { FullScreenLoader } from '@/components/LoadingSpinner'
import { FORGOT_PASSWORD_ROUTE } from '@/router/routes'
import store from '@/store'
import { EAuthActions } from '@/store/authStore/AuthStoreTypes'
import { EStoreModules } from '@/store/storeType'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { TwoFaLoginModel } from '../../types/models/TwoFaModel'
import TwoFaAuthenticatorLogin from './TwoFA/TwoFaAuthenticatorLogin.vue'

const router = useRouter()

const email = ref('')
const password = ref('')
const submitted = ref(false)
const trustDevice = ref()

const isLoading = computed(() => store.state.authentication.isLoading)
const temporaryToken = ref<string>()

const submitForm = () => {
    // Trigger the form submission
    if (!email.value || !password.value) return (submitted.value = true)
    submitted.value = false
    store
        .dispatch(`${EStoreModules.AUTHENTICATION}/${EAuthActions.LOGIN}`, {
            email: email.value,
            password: password.value
        })
        .then((res: TwoFaLoginModel) => {
            temporaryToken.value = res.temporary_token
        })
}
const handleConfirm = (code: string) =>
    store.dispatch(`${EStoreModules.AUTHENTICATION}/${EAuthActions.VERIFY_2FA}`, {
        temporary_token: temporaryToken.value,
        code,
        trust_device: trustDevice.value
    })

const errors = computed(() => ({
    emailError: submitted.value && !email.value,
    passwordError: submitted.value && !password.value
}))

const goToResetPassword = () => router.push(FORGOT_PASSWORD_ROUTE)
</script>

<template>
    <FullScreenLoader :isLoading="isLoading" />
    <GuestLayout
        :title="$t('authentication.title.title')"
        :primaryLabel="$t('authentication.login')"
        :secondaryLabel="$t('authentication.title.resetPassword')"
        @clickSecondary="goToResetPassword"
        @clickSubmit="submitForm">
        <template #content>
            <ReactiveLogo width="320" />
            <form method="post" name="login">
                <StackLayout :gap="32" direction="column">
                    <InputField
                        :label="$t('authentication.email')"
                        @keyup.enter="submitForm"
                        v-model="email"
                        @change="value => (email = value)"
                        :placeholder="$t('authentication.email')"
                        inputType="email"
                        required
                        autocomplete="username"
                        class="login-email"
                        :hasError="errors.emailError" />
                    <InputField
                        :label="$t('authentication.password')"
                        @keyup.enter="submitForm"
                        v-model="password"
                        @change="value => (password = value)"
                        :placeholder="$t('authentication.password')"
                        inputType="password"
                        required
                        autocomplete="current-password"
                        :hasError="errors.passwordError"
                        v-bind:password-reveal="true" />
                </StackLayout>
            </form>
        </template>
    </GuestLayout>
    <TwoFaAuthenticatorLogin
        :open="!!temporaryToken"
        :loading="isLoading"
        @confirm="handleConfirm"
        @close="temporaryToken = undefined" />
</template>

<style scoped lang="scss">
.login-email {
    & :deep(.help) {
        max-width: 300px;
    }
}
</style>
